import { useCallback, useRef, useEffect } from 'react';
import { getWorkerManager, areWorkersSupported } from './worker-manager';
import {
  formatHttpRequest,
  formatHttpResponse,
  isValidHttpStatusCode,
  normalizeHttpVersionNumber,
  parseHttpRequest,
  parseHttpResponse,
} from '@terra/shared-utils';

function formatRequestSync(request: {
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  rawHeaders?: string[];
  body?: string;
  httpVersion?: string;
}): string {
  return formatHttpRequest({
    ...request,
    httpVersion: normalizeHttpVersionNumber(request.httpVersion),
  });
}

function parseRequestSync({
  text,
  originalUrl,
}: {
  text: string;
  originalUrl: string;
}): ParsedRequest | null {
  const parsed = parseHttpRequest(text, { originalUrl });
  if (!parsed) {
    return null;
  }

  return {
    method: parsed.method,
    url: parsed.url,
    headers: parsed.headers,
    body: parsed.body,
    httpVersion: parsed.httpVersion,
  };
}

function formatResponseSync(response: {
  statusCode: number;
  statusMessage?: string;
  headers: Record<string, string | string[]>;
  body?: string;
  httpVersion?: string;
}): string {
  if (!isValidHttpStatusCode(response.statusCode)) {
    return '';
  }

  return formatHttpResponse({
    statusCode: response.statusCode,
    statusMessage: response.statusMessage || '',
    headers: response.headers,
    body: response.body,
    httpVersion: normalizeHttpVersionNumber(response.httpVersion),
  });
}

function parseResponseSync({ text }: { text: string }): ParsedResponse | null {
  return parseHttpResponse(text);
}

export type FormatRequestInput = {
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  rawHeaders?: string[];
  body?: string;
  httpVersion?: string;
};

export type ParseRequestInput = {
  text: string;
  originalUrl: string;
};

export type FormatResponseInput = {
  statusCode: number;
  statusMessage?: string;
  headers: Record<string, string | string[]>;
  body?: string;
  httpVersion?: string;
};

export type ParsedRequest = {
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  body: string;
  httpVersion: string;
};

export type ParsedResponse = {
  statusCode: number;
  statusMessage: string;
  headers: Record<string, string | string[]>;
  body: string;
  httpVersion: string;
};

export function useHttpParser() {
  const useWorkerRef = useRef(areWorkersSupported());
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const formatRequest = useCallback(
    async (input: FormatRequestInput): Promise<string> => {
      const bodySize = input.body?.length ?? 0;
      if (!useWorkerRef.current || bodySize < 10000) {
        return formatRequestSync(input);
      }

      const manager = getWorkerManager();
      if (!manager) {
        return formatRequestSync(input);
      }

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const result = await manager.sendMessage<FormatRequestInput, string>(
          'http-parser',
          'formatRequest',
          input,
        );
        if (signal.aborted) return '';
        return result;
      } catch (error) {
        if (signal.aborted) return '';
        console.warn(
          'Worker formatRequest failed, using sync fallback:',
          error,
        );
        return formatRequestSync(input);
      }
    },
    [],
  );

  const parseRequest = useCallback(
    async (input: ParseRequestInput): Promise<ParsedRequest | null> => {
      if (!useWorkerRef.current || input.text.length < 10000) {
        return parseRequestSync(input);
      }

      const manager = getWorkerManager();
      if (!manager) {
        return parseRequestSync(input);
      }

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const result = await manager.sendMessage<
          ParseRequestInput,
          ParsedRequest
        >('http-parser', 'parseRequest', input);
        if (signal.aborted) return null;
        return result;
      } catch (error) {
        if (signal.aborted) return null;
        console.warn('Worker parseRequest failed, using sync fallback:', error);
        return parseRequestSync(input);
      }
    },
    [],
  );

  const formatResponse = useCallback(
    async (input: FormatResponseInput): Promise<string> => {
      const bodySize = input.body?.length ?? 0;
      if (!useWorkerRef.current || bodySize < 10000) {
        return formatResponseSync(input);
      }

      const manager = getWorkerManager();
      if (!manager) {
        return formatResponseSync(input);
      }

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const result = await manager.sendMessage<FormatResponseInput, string>(
          'http-parser',
          'formatResponse',
          input,
        );
        if (signal.aborted) return '';
        return result;
      } catch (error) {
        if (signal.aborted) return '';
        console.warn(
          'Worker formatResponse failed, using sync fallback:',
          error,
        );
        return formatResponseSync(input);
      }
    },
    [],
  );

  const parseResponse = useCallback(
    async (input: { text: string }): Promise<ParsedResponse | null> => {
      if (!useWorkerRef.current || input.text.length < 10000) {
        return parseResponseSync(input);
      }

      const manager = getWorkerManager();
      if (!manager) {
        return parseResponseSync(input);
      }

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const result = await manager.sendMessage<
          { text: string },
          ParsedResponse
        >('http-parser', 'parseResponse', input);
        if (signal.aborted) {
          return null;
        }
        return result;
      } catch (error) {
        if (signal.aborted) {
          return null;
        }
        console.warn(
          'Worker parseResponse failed, using sync fallback:',
          error,
        );
        return parseResponseSync(input);
      }
    },
    [],
  );

  return {
    formatRequest,
    parseRequest,
    formatResponse,
    parseResponse,
    formatRequestSync,
    parseRequestSync,
    formatResponseSync,
    parseResponseSync,
    isWorkerSupported: useWorkerRef.current,
  };
}

export {
  formatRequestSync as formatRequestToText,
  parseRequestSync as parseRequestFromText,
  formatResponseSync as formatResponseToText,
  parseResponseSync as parseResponseFromText,
};
